import React, { FC } from 'react';
import { TypographyWithElipsis } from '@components/TypographyElipsis';
import { Box, MenuItem, Stack, Typography } from '@mui/material';
import { ArrowBigRightIcon } from '@utils/iconsDefs';

type Props = {
  label: string;
  badge?: number;
  selected?: boolean;
  onClick: () => void;
};

/**
 * One navigation row of a filter panel: a single-line name, a count badge
 * while `badge` is above zero, and a chevron.
 *
 * It is the Controls list of the CIS-18 reference panel, where a row opens
 * a Control's side panel rather than toggling a value, so it carries no
 * checkbox and never a count pill — navigation rows show no analytics
 * count. The badge is the funnel's drawing at 20px, the "green badge with
 * the number of selected values" of AC3.4: the Control and each of its
 * Safeguards counted once. `selected` marks the row whose side panel is
 * open, on the row's hover background, as the criteria list marks its
 * open row. The name truncates on one line with the full name in a
 * tooltip (AC4). Row padding, gap and hover come from the `MuiMenuItem`
 * theme override.
 */
const FilterNavRow: FC<Props> = ({
  label,
  badge = 0,
  selected = false,
  onClick,
}) => (
  <MenuItem
    onClick={onClick}
    selected={selected}
    sx={{ '&.Mui-selected, &.Mui-selected:hover': { bgcolor: 'grey.400' } }}
  >
    <TypographyWithElipsis
      variant="body1"
      color="grey.900"
      tooltipPlacement="bottom"
      sx={{ flex: 1, minWidth: 0 }}
    >
      {label}
    </TypographyWithElipsis>

    {badge > 0 && (
      <Stack
        data-testid="filter-nav-row-badge"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        sx={{
          width: 20,
          height: 20,
          borderRadius: '50%',
          bgcolor: 'primary.main',
        }}
      >
        <Typography variant="body2" color="grey.1000">
          {badge}
        </Typography>
      </Stack>
    )}

    <Box display="flex" width={20} height={20} flexShrink={0}>
      <ArrowBigRightIcon width={20} height={20} />
    </Box>
  </MenuItem>
);

export default FilterNavRow;
